import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Card,
  EditPane,
  Field,
  Input,
  MenuButton,
  Select,
  Switch,
  Textarea,
  cn,
  toast,
  useConfirm,
  type EditPaneTab,
  type MenuItem,
} from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionOrderComponentClient, ProductionOrderOperationClient } from "../../api/floor";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { squareMetres } from "./ScrapTable";
import { WarehouseClient } from "../../api/product";
import {
  AssemblyOrderClient,
  ON_THE_FLOOR,
  ProductionOrderClient,
  ProductionOrderMaterialClient,
  type ProductionOrderDTO,
  type ProductionOrderMaterialDTO,
} from "../../api/production";
import { NexpimView } from "../../views";
import { StockMovementsGrid } from "../manufacturing/StockMovementsGrid";
import { ProductionStatusBadge, productionKindLabel, productionStatusLabel, SigmBadge } from "./badges";
import { PutAwayDialog, SplitPanel } from "./assembly";
import { IssueDialog, OutputDialog } from "./PostDialogs";
import { OpenRecord } from "../../components/OpenRecord";
import { LotDocumentsTab } from "./LotDocumentsTab";
import { ComponentsTab } from "./lot/ComponentsTab";
import { PanelPlanEditor } from "./PanelPlanEditor";
import { CutJobStrip, OrderCutJobsTab } from "./cutjobs/OrderCutJobsTab";
import { EngineeringBomsTab } from "./lot/EngineeringBomsTab";
import { HistoryTab } from "./lot/HistoryTab";
import { AdjustMaterialDialog, CompleteDialog, HoldDialog } from "./lot/LotDialogs";
import { MaterialLogTab } from "./lot/MaterialLogTab";
import { NotesTab } from "./lot/NotesTab";
import { LotHeader } from "./lot/LotHeader";
import { isLate, when } from "./lot/lotModel";
import { OverviewTab } from "./lot/OverviewTab";
import { ScrapTab } from "./lot/ScrapTab";
import { TransfersTab } from "./lot/TransfersTab";

/**
 * One order: where it is in its life, how far each component or step is, what
 * it needs, its trucks, its documents and its history.
 *
 * <p>What can be done follows the kind and the status, the same rules the
 * server enforces. A production order: its production BOM is prepared and
 * confirmed, then it is released (or regenerated before confirming, or
 * deleted); on the floor it is marked produced (or sent back), ships on trucks
 * and closes with the last one (or by hand, short), or, if nothing happened
 * yet, is cancelled with its assembly orders; its Assembly tab shares its sets
 * between the versions. An assembly order is firmed by the sets that arrive,
 * worked step by step and put away a truckload at a time. A kanban or service
 * lot completes at Laval. A finished one can be reopened. An order made from
 * SIGM is locked: no regenerating, confirming, cancelling or deleting. Holding
 * an order flags it everywhere without moving it.</p>
 */
export function ProductionOrderDetailPage({ orderId }: { orderId: number }) {
  // Keyed on the order: following a parent or component-run link reuses this route, and the
  // open dialogs and unsaved header edits belong to the order they were started on.
  return <ProductionOrderDetail key={orderId} orderId={orderId} />;
}

type Action = "release" | "produced" | "backToProduction" | "cancel" | "reopen" | "regenerate";

function ProductionOrderDetail({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.PRODUCTION_ORDERS);
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const materials = useMemo(() => new ProductionOrderMaterialClient(http), [http]);
  const [issuing, setIssuing] = useState(false);
  const [producing, setProducing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [holding, setHolding] = useState(false);
  const [puttingAway, setPuttingAway] = useState(false);
  const [adjusting, setAdjusting] = useState<ProductionOrderMaterialDTO | null>(null);
  // The Order tab's unsaved edits live here, not in the tab: switching tabs unmounts it,
  // and a refetch after a material edit must not wipe what was typed.
  const [headerEdits, setHeaderEdits] = useState<HeaderEdits>({});

  const order = useQuery({
    queryKey: ["production", "production-order", orderId],
    queryFn: () => orders.findById(orderId),
    enabled: allowed,
  });
  const orderMaterials = useQuery({
    queryKey: ["production", "production-order-material", orderId],
    queryFn: async () =>
      (await materials.find({ page: 0, size: 2000, filters: { production_order_id: [String(orderId)] }, sort: ["position,asc"] }))
        .content,
    enabled: allowed,
  });
  // The floor plan exists once the lot is released; an imported SIGM run may have one too.
  const floorPlanned = order.data != null && order.data.status !== "PLANNED";
  const components = useQuery({
    queryKey: ["production", "production-order-component", orderId],
    queryFn: () => new ProductionOrderComponentClient(http).forLot(orderId),
    enabled: allowed && floorPlanned,
  });
  const steps = useQuery({
    queryKey: ["production", "production-order-operation", orderId],
    queryFn: () => new ProductionOrderOperationClient(http).forLot(orderId),
    enabled: allowed && floorPlanned,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["production"] });
    await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
  };

  const act = useMutation({
    mutationFn: (action: Action) => orders[action](orderId),
    onSuccess: async (updated) => {
      await refresh();
      toast.success(
        t("mo.now", "Order {{n}} is now {{status}}", { n: updated.number, status: productionStatusLabel(t, updated.status).toLowerCase() }),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const remove = useMutation({
    mutationFn: () => orders.remove(orderId),
    onSuccess: async () => {
      await refresh();
      toast.success(t("notification.deleted", "Deleted"));
      void navigate({ to: "/production/orders" });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const releaseHold = useMutation({
    mutationFn: (lot: ProductionOrderDTO) => orders.update({ ...lot, is_on_hold: false, hold_reason: null }),
    onSuccess: async () => {
      await refresh();
      toast.success(t("lot.hold.released", "Hold released"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  // Confirming the production BOM is the step between planning and release; undoing it reopens the lines.
  const confirmation = useMutation({
    mutationFn: (confirmed: boolean) => (confirmed ? orders.confirmMaterials(orderId) : orders.unconfirmMaterials(orderId)),
    onSuccess: async (_updated, confirmed) => {
      await refresh();
      toast.success(confirmed ? t("lot.bom.confirmed-toast", "Production BOM confirmed") : t("lot.bom.unconfirmed-toast", "Production BOM back in preparation"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const completeAssembly = useMutation({
    mutationFn: () => new AssemblyOrderClient(http).complete(orderId),
    onSuccess: async (updated) => {
      await refresh();
      toast.success(t("mo.now", "Order {{n}} is now {{status}}", { n: updated.number, status: productionStatusLabel(t, updated.status).toLowerCase() }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const backflush = useMutation({
    mutationFn: () => orders.backflush(orderId),
    onSuccess: async (result) => {
      await refresh();
      toast.success(t("lot.backflush.done", "{{n}} materials consumed", { n: result.movements }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.production-orders", "Production orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const data = order.data;
  const parent = { to: "/production/orders", label: t("navigation.item.production-orders", "Production orders") };
  if (!data) {
    return (
      <>
        <PageHeader parent={parent} title={t("navigation.item.production-orders", "Production orders")} />
        <div className="p-4 text-[13px] text-ink-muted lg:p-6">
          {order.isLoading ? t("common.loading", "Loading…") : t("mo.not-found", "That order could not be loaded.")}
        </div>
      </>
    );
  }

  const rows = orderMaterials.data ?? [];
  const lotComponents = components.data ?? [];
  const lotOperations = steps.data ?? [];
  const onFloor = ON_THE_FLOOR.includes(data.status);
  const native = !data.is_from_sigm;
  const isAssembly = data.kind === "ASSEMBLY";
  const isFabrication = data.kind === "FABRICATION";
  const finished = data.status === "COMPLETED" || data.status === "CLOSED" || data.status === "CANCELLED";
  // An assembly order's units packed: the least packed of its boxes; what is not shelved yet can be put away.
  const packSteps = lotOperations.filter((step) => step.production_order_component_id == null && step.process_code === "PACK");
  const packed = packSteps.length === 0 ? 0 : Math.min(...packSteps.map((step) => Number(step.quantity_done ?? 0)));
  const toPutAway = packed - Number(data.quantity_put_away ?? 0);
  // One action at a time: a second click while the first is on its way would act on a stale status.
  const busy =
    act.isPending || remove.isPending || releaseHold.isPending || backflush.isPending || confirmation.isPending || completeAssembly.isPending;
  const confirmed = data.materials_confirmed_at != null;
  // Release waits for the production BOM, but for a service lot (its components are the parts) and SIGM's imports.
  const needsConfirmation = native && data.kind !== "SERVICE" && !confirmed;
  const produced = Number(data.quantity_produced ?? 0);
  const floorLoading = components.isLoading || steps.isLoading;
  // Completing is the next step once a service or kanban lot is made; a production order closes by itself
  // with its last truck, so closing it short stays in the menu, as completing SIGM's runs does.
  const completeIsNext = (data.kind === "KANBAN" || data.kind === "SERVICE") && data.status === "PRODUCED";

  const ask = async (action: "cancel" | "regenerate" | "produced", title: string, message: string) => {
    if (await confirm({ title, message, confirmLabel: title, variant: action === "cancel" ? "danger" : "primary" })) {
      act.mutate(action);
    }
  };

  // Secondary actions, in a menu so the header keeps one or two clear next steps.
  const more: MenuItem[] = [];
  if (canWrite) {
    if (data.status === "PLANNED" && !confirmed && native && !isAssembly) {
      more.push({
        label: t("mo.regenerate", "Regenerate materials"),
        onClick: () =>
          void ask(
            "regenerate",
            t("mo.regenerate", "Regenerate materials"),
            t("mo.regenerate.message", "Replaces every material line with today's active bill of materials. Changes made to the lines are lost."),
          ),
      });
    }
    if (data.status === "PLANNED" && native && !isAssembly) {
      more.push({
        label: t("po.delete", "Delete"),
        onClick: async () => {
          if (
            await confirm({
              title: t("mo.delete.title", "Delete this order?"),
              message: isFabrication
                ? t("mo.delete.message.fabrication", "A planned order has not been on the floor, so it can go entirely, with its assembly orders. Its lot goes back to the pipeline.")
                : t("mo.delete.message", "A planned order has not been on the floor, so it can go entirely."),
              variant: "danger",
              confirmLabel: t("po.delete", "Delete"),
            })
          ) {
            remove.mutate();
          }
        },
      });
    }
    if (data.status === "PRODUCED") {
      more.push({ label: t("lot.back-to-production", "Back to production"), onClick: () => act.mutate("backToProduction") });
    }
    if (onFloor) {
      more.push({ label: t("mo.issue", "Issue"), onClick: () => setIssuing(true) });
      if (data.kind === "KANBAN" || data.kind === "SERVICE") more.push({ label: t("mo.output", "Post output"), onClick: () => setProducing(true) });
      if (native) more.push({ label: t("lot.backflush", "Consume materials now"), onClick: () => backflush.mutate() });
      if (isAssembly) {
        more.push({
          label: t("assembly.complete", "Complete short"),
          onClick: async () => {
            if (
              await confirm({
                title: t("assembly.complete", "Complete short"),
                message: t("assembly.complete.message", "Nothing more will be boxed on this order: what was not packed and put away stays short."),
                confirmLabel: t("assembly.complete", "Complete short"),
              })
            ) {
              completeAssembly.mutate();
            }
          },
        });
      } else if (!completeIsNext) {
        more.push({ label: isFabrication ? t("mo.close", "Close") : t("mo.complete", "Complete"), onClick: () => setCompleting(true) });
      }
    }
    if ((data.status === "PLANNED" || data.status === "RELEASED") && native && !isAssembly) {
      more.push({
        label: t("mo.cancel", "Cancel order"),
        onClick: () =>
          void ask(
            "cancel",
            t("mo.cancel", "Cancel order"),
            isFabrication
              ? t("lot.cancel.message.fabrication", "Refused if anything has been issued, produced or reported on the floor. Its assembly orders are cancelled with it; it stays on file as cancelled.")
              : t("lot.cancel.message", "Refused if anything has been issued, produced or reported on the floor. It stays on file as cancelled."),
          ),
      });
    }
    if (!finished) {
      more.push(
        data.is_on_hold
          ? { label: t("lot.hold.release", "Release hold"), onClick: () => releaseHold.mutate(data) }
          : { label: t("lot.hold", "Put on hold"), onClick: () => setHolding(true) },
      );
    }
  }

  const tabs: EditPaneTab[] = [
    {
      id: "overview",
      label: t("lot.tab.overview", "Floor"),
      hidden: data.status === "PLANNED",
      badge:
        (data.missing_count ?? 0) > 0 ? (
          <Badge variant="error" size="sm">
            {data.missing_count}
          </Badge>
        ) : undefined,
      content: (
        <>
        {/* The cut jobs its pieces are cut in (docs/cutting-jobs §9.4). */}
        <CutJobStrip lot={data} />
        <OverviewTab
          lot={data}
          components={lotComponents}
          steps={lotOperations}
          loading={floorLoading}
          editable={canWrite && onFloor}
          onChanged={refresh}
        />
        </>
      ),
    },
    {
      id: "materials",
      flush: true,
      label: t("lot.tab.production-bom", "Production BOM"),
      badge: data.material_open_count ?? 0,
      content: (
        <div className="flex min-h-0 flex-1 flex-col">
          <ConfirmationStrip
            order={data}
            canConfirm={canWrite && data.status === "PLANNED"}
            busy={busy}
            pending={confirmation.isPending}
            onConfirm={(value) => confirmation.mutate(value)}
          />
          {data.status === "PLANNED" && (
            <p className="border-b border-line px-4 py-2 text-[12px] text-ink-muted lg:px-6">
              {isAssembly
                ? t("lot.planned-hint.assembly", "Planned with its production order: demand for its packaging. Its steps are written when its first sets arrive at Terrebonne.")
                : t("lot.planned-hint.v2", "Planned: the floor plan (components and their steps) is written when the order is released.")}
            </p>
          )}
          {/* Which real panels each material is cut from, and how many (docs/cutting-jobs §4). */}
          <PanelPlanEditor
            orderId={data.id}
            editable={canWrite && native && (isAssembly ? data.status === "PLANNED" || data.status === "FIRMED" : data.status === "PLANNED" && !confirmed)}
            onChanged={refresh}
          />
          <MaterialsTab
            rows={rows}
            editable={canWrite && !finished}
            // The database locks the required quantities once confirmed; SIGM's imports are never confirmed.
            quantityEditable={!confirmed}
            warehouseEditable={canWrite && data.status === "PLANNED"}
            adjustable={canWrite && onFloor}
            onAdjust={setAdjusting}
            onChanged={refresh}
          />
        </div>
      ),
    },
    {
      // The cut jobs its raw panels are cut in, managed from here (docs/cutting-jobs §6).
      id: "cut-jobs",
      flush: true,
      label: t("lot.tab.cut-jobs", "Cut jobs"),
      hidden: !native,
      content: <OrderCutJobsTab lot={data} />,
    },
    {
      // The engineering BOM revisions it was made from (0073).
      id: "engineering-boms",
      flush: true,
      label: t("lot.tab.engineering-boms", "Engineering BOMs"),
      content: <EngineeringBomsTab lot={data} />,
    },
    {
      id: "material-log",
      flush: true,
      label: t("lot.tab.consumption", "Consumption"),
      hidden: data.status === "PLANNED" || !native,
      content: (
        <MaterialLogTab lot={data} canAddUnplanned={canWrite && data.status !== "PLANNED" && data.status !== "CANCELLED"} onChanged={refresh} />
      ),
    },
    {
      id: "scrap",
      flush: true,
      label: t("lot.tab.scrap", "Scrap"),
      hidden: data.status === "PLANNED" || !native,
      content: <ScrapTab lot={data} />,
    },
    {
      id: "components",
      flush: true,
      label: t("lot.tab.components", "Components"),
      hidden: data.status === "PLANNED" || lotComponents.length === 0,
      badge: lotComponents.length,
      content: <ComponentsTab components={lotComponents} loading={floorLoading} editable={canWrite} onChanged={refresh} />,
    },
    {
      id: "transfers",
      label: t("lot.tab.transfers", "Transfers"),
      hidden: !isFabrication || data.status === "PLANNED" || !native,
      content: <TransfersTab lot={data} components={lotComponents} editable={canWrite} onChanged={refresh} />,
    },
    {
      id: "assembly",
      label: t("lot.tab.assembly", "Assembly"),
      hidden: !isFabrication,
      content: <SplitPanel fabricationOrderId={data.id} editable={canWrite && native} />,
    },
    {
      id: "notes",
      // NotesPanel pads itself, as on the part page.
      flush: true,
      label: t("lot.tab.notes", "Notes"),
      content: <NotesTab orderId={orderId} editable={canWrite} />,
    },
    {
      id: "documents",
      label: t("lot.tab.documents", "Documents"),
      hidden: data.status === "PLANNED",
      content: <LotDocumentsTab order={data} />,
    },
    {
      id: "history",
      flush: true,
      label: t("lot.tab.history", "History"),
      hidden: data.status === "PLANNED",
      content: <HistoryTab lotId={orderId} />,
    },
    {
      id: "order",
      label: t("mo.tab.order", "Order"),
      content: (
        <HeaderTab
          order={data}
          edits={headerEdits}
          onEdit={setHeaderEdits}
          editable={canWrite && data.status !== "CANCELLED"}
          onSaved={refresh}
        />
      ),
    },
    {
      id: "movements",
      flush: true,
      label: t("mo.tab.movements", "Movements"),
      content: <StockMovementsGrid productionOrderId={orderId} />,
    },
  ];
  if ((data.child_count ?? 0) > 0) {
    tabs.push({
      id: "children",
      flush: true,
      label: t("mo.tab.children.runs", "Component runs"),
      badge: data.child_count,
      content: <ChildrenTab orderId={orderId} />,
    });
  }

  return (
    <>
      <PageHeader
        parent={parent}
        title={t("mo.title", "Order {{n}}", { n: data.number })}
        subtitle={[data.part_number, data.part_name ?? data.name, `${formatNumber(data.quantity)} ${data.stock_unit_code ?? ""}`.trim()]
          .filter(Boolean)
          .join(" · ")}
        badges={
          <>
            <ProductionStatusBadge status={data.status} />
            <Badge variant="neutral" size="sm">
              {productionKindLabel(t, data.kind)}
            </Badge>
            {data.priority != null && (
              <Badge variant="accent" size="sm">
                {t("lot.priority-n", "Priority {{n}}", { n: data.priority })}
              </Badge>
            )}
            {data.due_date && (
              <Badge variant={isLate(data) ? "error" : "neutral"} size="sm">
                {isLate(data) ? t("lot.late", "Late: due {{date}}", { date: data.due_date }) : t("lot.due", "Due {{date}}", { date: data.due_date })}
              </Badge>
            )}
            {produced > 0 && (
              <Badge variant={produced >= Number(data.quantity) ? "success" : "neutral"} size="sm">
                {t("mo.produced-of", "{{produced}} of {{quantity}} made", { produced: formatNumber(produced), quantity: formatNumber(data.quantity) })}
              </Badge>
            )}
            {data.storage_location_code && (
              <Badge variant="neutral" size="sm">
                {t("lot.packing.shelf", "Shelf {{code}}", { code: data.storage_location_code })}
              </Badge>
            )}
            {data.version_code && (
              <Badge variant="neutral" size="sm">
                {t("mo.version-n", "Version {{code}}", { code: data.version_code })}
              </Badge>
            )}
            {isAssembly && (
              <Badge variant={Number(data.firmed_quantity ?? 0) >= Number(data.quantity) ? "success" : "warning"} size="sm">
                {t("mo.firmed-of", "{{firmed}} of {{quantity}} sets arrived", {
                  firmed: formatNumber(data.firmed_quantity ?? 0),
                  quantity: formatNumber(data.quantity),
                })}
              </Badge>
            )}
            {!native && <SigmBadge />}
            {data.fabrication_order_id != null && (
              <Link
                to="/production/orders/$orderId"
                params={{ orderId: String(data.fabrication_order_id) }}
                className="text-[12px] text-accent underline-offset-2 hover:underline"
              >
                {t("mo.from-order", "Sets of order {{n}}", { n: data.fabrication_order_number ?? "" })}
              </Link>
            )}
            {data.parent_production_order_id != null && (
              <Link
                to="/production/orders/$orderId"
                params={{ orderId: String(data.parent_production_order_id) }}
                className="text-[12px] text-accent underline-offset-2 hover:underline"
              >
                {t("mo.parent-run", "Part of a run")}
              </Link>
            )}
          </>
        }
        actions={
          canWrite ? (
            <>
              {more.length > 0 && <MenuButton variant="ghost" label={t("lot.more", "More")} icon={<SlidersHorizontal className="size-4" />} items={more} disabled={busy} />}
              {data.status === "PLANNED" && !isAssembly && (
                // A disabled button fires no hover, so the reason sits on its wrapper.
                <span title={needsConfirmation ? t("lot.bom.release-needs", "Confirm the production BOM first (Production BOM tab).") : undefined}>
                  <Button
                    variant="primary"
                    disabled={busy || needsConfirmation}
                    loading={act.isPending && act.variables === "release"}
                    onClick={() => act.mutate("release")}
                  >
                    {t("mo.release", "Release")}
                  </Button>
                </span>
              )}
              {data.status === "RELEASED" && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void ask(
                      "produced",
                      t("lot.mark-produced", "Mark produced"),
                      t("lot.mark-produced.message", "Every component is made at Laval. The floor does this itself when the last step is reported."),
                    )
                  }
                >
                  {t("lot.mark-produced", "Mark produced")}
                </Button>
              )}
              {isAssembly && (data.status === "FIRMED" || data.status === "IN_PROGRESS") && toPutAway > 0 && (
                <Button variant="primary" disabled={busy} onClick={() => setPuttingAway(true)}>
                  {t("assembly.put-away.confirm", "Put away")}
                </Button>
              )}
              {onFloor && completeIsNext && (
                <Button variant="primary" disabled={busy} onClick={() => setCompleting(true)}>
                  {t("mo.complete", "Complete")}
                </Button>
              )}
              {(data.status === "COMPLETED" || data.status === "CLOSED") && (
                <Button variant="ghost" disabled={busy} loading={act.isPending} onClick={() => act.mutate("reopen")}>
                  {t("po.reopen", "Reopen")}
                </Button>
              )}
            </>
          ) : null
        }
      />
      <EditPane
        tabs={tabs}
        urlParam="tab"
        initialTab={data.status === "PLANNED" ? "materials" : "overview"}
        header={<LotHeader lot={data} steps={lotOperations} />}
      />
      {issuing && <IssueDialog order={data} materials={rows} onClose={() => setIssuing(false)} onPosted={refresh} />}
      {producing && <OutputDialog order={data} onClose={() => setProducing(false)} onPosted={refresh} />}
      {completing && (
        <CompleteDialog
          lot={data}
          onClose={() => setCompleting(false)}
          onDone={async (updated) => {
            setCompleting(false);
            await refresh();
            toast.success(t("mo.now", "Order {{n}} is now {{status}}", { n: updated.number, status: productionStatusLabel(t, updated.status).toLowerCase() }));
          }}
        />
      )}
      {puttingAway && <PutAwayDialog order={data} packed={packed} onClose={() => setPuttingAway(false)} />}
      {holding && (
        <HoldDialog
          lot={data}
          onClose={() => setHolding(false)}
          onDone={async () => {
            setHolding(false);
            await refresh();
          }}
        />
      )}
      {adjusting && (
        <AdjustMaterialDialog
          lot={data}
          material={adjusting}
          onClose={() => setAdjusting(null)}
          onDone={async () => {
            setAdjusting(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

function MaterialsTab({
  rows,
  editable,
  quantityEditable,
  warehouseEditable,
  adjustable,
  onAdjust,
  onChanged,
}: {
  rows: ProductionOrderMaterialDTO[];
  editable: boolean;
  /** Until the production BOM is confirmed: the office moves sheets from a default panel to an alternative. */
  quantityEditable: boolean;
  /** Only while PLANNED: once issuing starts, a line's stock has already left from somewhere. */
  warehouseEditable: boolean;
  /** On the floor: what was really used can be corrected, which the backflush respects. */
  adjustable: boolean;
  onAdjust: (material: ProductionOrderMaterialDTO) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const materials = useMemo(() => new ProductionOrderMaterialClient(http), [http]);
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    enabled: warehouseEditable,
    staleTime: 30 * 60_000,
  });
  // Where materials can be drawn from: never a distribution warehouse, which holds Nexera's sellable stock.
  const drawFrom = (warehouses.data ?? [])
    .filter((warehouse) => warehouse.is_active && warehouse.kind !== "DISTRIBUTION")
    .map((warehouse) => ({ value: String(warehouse.id), label: warehouse.code }));

  // The panels of one virtual panel are one group: its order on the page, its first line, and the area it needs.
  const groups = useMemo(() => {
    const index = new Map<number, number>();
    const first = new Set<number>();
    const area = new Map<number, number>();
    for (const row of rows) {
      if (row.virtual_part_id == null || row.is_unplanned) continue;
      if (!index.has(row.virtual_part_id)) {
        index.set(row.virtual_part_id, index.size);
        first.add(row.id);
      }
      area.set(row.virtual_part_id, (area.get(row.virtual_part_id) ?? 0) + Number(row.engineering_quantity ?? 0));
    }
    return { index, first, area };
  }, [rows]);

  const cols = useMemo(() => {
    const c = defineColumns<ProductionOrderMaterialDTO>();
    return [
      c.number("position", (row) => row.position, { headerKey: "column.bom.position", priority: 4 }),
      c.custom(
        "part",
        (row) => row.part_number ?? "",
        (row) => (
          <span style={{ paddingLeft: `${String((row.level - 1) * 16)}px` }}>
            <span className={row.is_made ? "font-medium" : undefined}>{row.part_number}</span>
            <span className="ml-2 text-[11px] text-ink-faint">{row.part_name}</span>
            {row.is_made && (
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("mo.material.made", "made here")}
              </Badge>
            )}
            {row.box_number != null && (
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("mo.material.box", "box {{n}}", { n: row.box_number })}
              </Badge>
            )}
            {row.supply === "FROM_STOCK" && (
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("mo.material.from-stock", "from stock")}
              </Badge>
            )}
            {row.inventory_policy === "UNTRACKED" && (
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("mo.material.untracked", "not tracked")}
              </Badge>
            )}
            {row.virtual_part_id != null && !isDefaultPanel(row) && (
              <Badge variant="neutral" size="sm" className="ml-2">
                {t("lot.bom.alternative", "alternative")}
              </Badge>
            )}
            {row.is_unplanned && (
              <span title={row.unplanned_reason ?? undefined}>
                <Badge variant="warning" size="sm" className="ml-2">
                  {t("lot.bom.unplanned", "Unplanned")}
                </Badge>
              </span>
            )}
            {row.is_consumed_whole && !row.is_made && (
              <span title={t("mo.material.whole.title", "Consumed in whole units: a panel started is a panel used.")}>
                <Badge variant="neutral" size="sm" className="ml-2">
                  {t("mo.material.whole", "whole")}
                </Badge>
              </span>
            )}
          </span>
        ),
        { headerKey: "column.bom.component", priority: 1 },
      ),
      // Right after the component: picking a panel is read in its sheets.
      c.text("quantity_required", (row) => formatNumber(row.quantity_required), { headerKey: "column.mo.required", priority: 2 }),
      c.text("stock_unit_code", (row) => row.stock_unit_code ?? "", { headerKey: "column.bom.unit" }),
      // The virtual panel a real panel is one option of, with the area the engineering BOM needs of it:
      // written once, on the group's first line, so the group reads as one.
      c.custom(
        "virtual_part_number",
        (row) => row.virtual_part_number ?? "",
        (row) =>
          row.virtual_part_id == null || !groups.first.has(row.id) ? (
            <span />
          ) : (
            <span>
              <span className="font-medium text-ink">{row.virtual_part_number}</span>
              <span className="ml-2 text-[11px] text-ink-muted">{row.virtual_part_name}</span>
              <span className="ml-2 text-[11px] tabular-nums text-ink-muted">
                {t("lot.bom.engineering-area", "needs {{area}}", { area: squareMetres(groups.area.get(row.virtual_part_id) ?? 0) })}
              </span>
            </span>
          ),
        { header: t("lot.bom.virtual-panel", "Panel material"), priority: 2 },
      ),
      c.text("process_code", (row) => (row.is_made ? "" : (row.process_code ?? "")), { headerKey: "lot.material.consumed-at", priority: 3 }),
      c.text("operation_code", (row) => row.operation_code ?? "", { headerKey: "column.bom.operation", defaultVisible: false }),
      c.text("warehouse_code", (row) => (row.is_made ? "" : (row.warehouse_code ?? "")), {
        headerKey: "column.stock.warehouse",
        filter: "select",
        filterOptions: [...new Set(rows.map((row) => row.warehouse_code ?? ""))]
          .filter((code) => code !== "")
          .map((code) => ({ value: code, label: code })),
        priority: 3,
      }),
      c.custom(
        "quantity_issued",
        (row) => row.quantity_issued ?? 0,
        (row) =>
          row.is_made ? (
            <span />
          ) : (
            <span className="tabular-nums">
              {formatNumber(row.quantity_issued)}
              {Number(row.quantity_remaining ?? 0) > 0 && row.inventory_policy !== "UNTRACKED" && (
                <span className="ml-1.5 text-[11px] text-warn">
                  {t("mo.material.short", "{{n}} to issue", { n: formatNumber(row.quantity_remaining) })}
                </span>
              )}
            </span>
          ),
        { headerKey: "column.mo.issued", priority: 2 },
      ),
      c.text("quantity_adjustment", (row) => (Number(row.quantity_adjustment ?? 0) === 0 ? "" : `${Number(row.quantity_adjustment) > 0 ? "+" : ""}${formatNumber(row.quantity_adjustment)}`), {
        headerKey: "lot.material.adjusted",
        priority: 4,
      }),
      c.text("quantity_counted", (row) => (row.quantity_counted == null ? "" : formatNumber(row.quantity_counted)), {
        headerKey: "lot.material.counted",
        priority: 3,
      }),
      c.text("planned_unit_cost", (row) => (row.planned_unit_cost == null ? "" : formatNumber(row.planned_unit_cost)), {
        headerKey: "column.mo.planned-cost",
        defaultVisible: false,
      }),
      c.text("parent_part_number", (row) => row.parent_part_number ?? "", { headerKey: "column.mo.goes-into", defaultVisible: false }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [t, rows, groups]);

  return (
    <DataTable<ProductionOrderMaterialDTO>
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      // Each virtual panel's group is a band: tinted, alternating, a coloured stripe on the left and a
      // rule above its first line. A panel not chosen reads as the option it is, not a need.
      rowClassName={(row) => {
        const group = row.virtual_part_id == null || row.is_unplanned ? undefined : groups.index.get(row.virtual_part_id);
        if (group === undefined) return undefined;
        return cn(
          group % 2 === 0
            ? "bg-accent-soft [&>td:first-child]:shadow-[inset_4px_0_0_var(--color-accent)]"
            : "bg-ok-soft/60 [&>td:first-child]:shadow-[inset_4px_0_0_var(--color-ok)]",
          groups.first.has(row.id) && "[&>td]:border-t-2 [&>td]:border-t-line",
          !isDefaultPanel(row) && "text-ink-faint",
        );
      }}
      rowActions={
        adjustable
          ? (row) =>
              row.is_made || row.inventory_policy === "UNTRACKED" ? null : (
                <Button size="sm" variant="ghost" onClick={() => onAdjust(row)}>
                  {t("lot.adjust", "Adjust used")}
                </Button>
              )
          : undefined
      }
      editor={
        editable
          ? {
              fields: {
                ...(quantityEditable
                  ? {
                quantity_required: {
                  kind: "number" as const,
                  get: (row: ProductionOrderMaterialDTO) => (Number.isNaN(row.quantity_required) ? "" : row.quantity_required),
                  // "" stays NaN so validate refuses it rather than saving a zero nobody typed.
                  set: (row: ProductionOrderMaterialDTO, value: string) => ({ ...row, quantity_required: value.trim() === "" ? Number.NaN : Number(value) }),
                  required: true,
                },
                    }
                  : {}),
                note: {
                  kind: "text",
                  get: (row) => row.note,
                  set: (row, value) => ({ ...row, note: value === "" ? null : value }),
                },
                ...(warehouseEditable
                  ? {
                      warehouse_code: {
                        kind: "select" as const,
                        get: (row: ProductionOrderMaterialDTO) => String(row.warehouse_id),
                        set: (row: ProductionOrderMaterialDTO, value: string) => ({
                          ...row,
                          warehouse_id: Number(value),
                          warehouse_code: drawFrom.find((option) => option.value === value)?.label ?? row.warehouse_code,
                        }),
                        options: drawFrom,
                        required: true,
                      },
                    }
                  : {}),
              },
              validate: (row) => {
                const quantity = Number(row.quantity_required);
                return Number.isFinite(quantity) && quantity >= 0
                  ? null
                  : { quantity_required: t("mo.material.quantity-invalid", "Enter a quantity of zero or more.") };
              },
              onSave: async (row) => {
                try {
                  await materials.update(row);
                } catch (failure) {
                  // The row editor shows the error's message: give it the server's reason, not "HTTP 500".
                  throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                }
                await onChanged();
                toast.success(t("notification.saved", "Saved"));
              },
            }
          : undefined
      }
      emptyMessage={t("mo.materials.empty", "No materials: the part has no active bill of materials.")}
    />
  );
}

/**
 * The real panel a virtual panel's sheets were planned on: the engineering area sits
 * on it, and every other option of the virtual panel is an alternative at zero.
 */
function isDefaultPanel(row: ProductionOrderMaterialDTO): boolean {
  return row.virtual_part_id != null && Number(row.engineering_quantity ?? 0) > 0;
}

/**
 * Where the production BOM stands. Planned and not confirmed, the office moves
 * sheets from a default panel to an alternative, then confirms: release needs
 * it, and the lines are locked from then on.
 */
function ConfirmationStrip({
  order,
  canConfirm,
  busy,
  pending,
  onConfirm,
}: {
  order: ProductionOrderDTO;
  canConfirm: boolean;
  busy: boolean;
  pending: boolean;
  onConfirm: (confirmed: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  if (order.sigm_project_id != null) {
    return <p className="border-b border-line px-4 py-2 text-[12px] text-ink-muted lg:px-6">{t("lot.bom.sigm", "Imported from SIGM")}</p>;
  }
  const confirmedAt = order.materials_confirmed_at;
  if (confirmedAt == null && order.status !== "PLANNED") return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-2 lg:px-6">
      {confirmedAt == null ? (
        <>
          <p className="text-[12px] text-ink-muted">
            {t("lot.bom.preparing", "Production BOM in preparation: pick the panel each material is cut from, then confirm.")}
          </p>
          {canConfirm && (
            <Button size="sm" variant="primary" disabled={busy} loading={pending} onClick={() => onConfirm(true)}>
              {t("lot.bom.confirm", "Confirm production BOM")}
            </Button>
          )}
        </>
      ) : (
        <>
          <p className="text-[12px] text-ink-muted">{t("lot.bom.confirmed-on", "Confirmed on {{date}}", { date: when(confirmedAt, i18n.language) })}</p>
          {canConfirm && (
            <Button size="sm" variant="ghost" disabled={busy} loading={pending} onClick={() => onConfirm(false)}>
              {t("lot.bom.unconfirm", "Undo confirmation")}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/** SIGM's child projects: one per manufactured component, each its own row. */
function ChildrenTab({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const children = useQuery({
    queryKey: ["production", "production-order", "children", orderId],
    queryFn: async () =>
      (await orders.find({ page: 0, size: 500, filters: { parent_production_order_id: [String(orderId)] }, sort: ["number,asc"] })).content,
  });
  const cols = useMemo(() => {
    const c = defineColumns<ProductionOrderDTO>();
    return [
      c.number("number", (row) => row.number, { headerKey: "column.mo.number", priority: 1 }),
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.part.name", priority: 3 }),
      c.custom("status", (row) => row.status, (row) => <ProductionStatusBadge status={row.status} />, { headerKey: "column.status" }),
      c.number("quantity", (row) => row.quantity, { headerKey: "column.mo.quantity", priority: 2 }),
      c.number("child_count", (row) => row.child_count, { headerKey: "mo.tab.children", defaultVisible: false }),
    ];
  }, [t]);
  return (
    <DataTable<ProductionOrderDTO>
      data={children.data ?? []}
      columns={cols}
      getRowId={(row) => String(row.id)}
      loading={children.isLoading}
      rowActions={(row) => <OpenRecord target={{ kind: "production-order", id: row.id }} label={String(row.number)} />}
      emptyMessage={t("table.empty", "Nothing to show")}
    />
  );
}

/**
 * The header fields being changed, over the order as loaded. The quantity and the
 * pipeline lot are kept as typed so a half-typed number is not coerced on the way.
 */
type HeaderEdits = Partial<Omit<ProductionOrderDTO, "quantity" | "production_pipeline_lot_id">> & {
  quantity?: string;
  lot?: string;
};

function HeaderTab({
  order,
  edits,
  onEdit,
  editable,
  onSaved,
}: {
  order: ProductionOrderDTO;
  edits: HeaderEdits;
  onEdit: (edits: HeaderEdits) => void;
  editable: boolean;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const { quantity: quantityText, lot: lotText, ...fields } = edits;
  const draft: ProductionOrderDTO = { ...order, ...fields };
  const planned = order.status === "PLANNED";

  // Quantity: only a planned run's, and only through regenerating its materials.
  const quantity = quantityText === undefined ? Number(order.quantity) : Number(quantityText);
  const quantityValid = quantityText === undefined || (quantityText.trim() !== "" && quantity > 0);
  // Regenerating is refused once the production BOM is confirmed, so is a new quantity.
  const regenerable = planned && order.materials_confirmed_at == null;
  const quantityChanged = regenerable && quantityValid && quantity !== Number(order.quantity);
  // Pipeline lot: a lot id, a whole number, or nothing.
  const lot = lotText === undefined ? order.production_pipeline_lot_id : lotText.trim() === "" ? null : Number(lotText);
  const lotValid = lot === null || lot === undefined || (Number.isInteger(lot) && lot > 0);
  const headerChanged =
    Object.entries(fields).some(([key, value]) => value !== order[key as keyof ProductionOrderDTO]) ||
    (lotValid && (lot ?? null) !== (order.production_pipeline_lot_id ?? null));
  const dirty = headerChanged || quantityChanged;
  const set = (patch: HeaderEdits) => onEdit({ ...edits, ...patch });

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (headerChanged) {
        await orders.update({ ...draft, quantity: order.quantity, production_pipeline_lot_id: lot ?? null });
      }
      if (quantityChanged) {
        await orders.regenerate(order.id, quantity);
      }
    },
    onSuccess: async () => {
      onEdit({});
      await onSaved();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const submit = async () => {
    if (
      quantityChanged &&
      !(await confirm({
        title: t("mo.quantity.confirm.title", "Change the quantity?"),
        message: t(
          "mo.quantity.confirm.message",
          "The materials are regenerated for {{n}} from today's active bill of materials. Changes made to the lines are lost.",
          { n: formatNumber(quantity) },
        ),
        confirmLabel: t("mo.regenerate", "Regenerate materials"),
      }))
    ) {
      return;
    }
    save.mutate();
  };

  const text = (value: string) => (value.trim() === "" ? null : value);
  const date = (id: string, label: string, key: "start_date" | "due_date" | "particle_cut_date" | "mdf_cut_date" | "arrived_laval_date") => (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        type="date"
        value={draft[key] ?? ""}
        disabled={!editable}
        onChange={(event) => set({ [key]: event.target.value || null })}
      />
    </Field>
  );

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t("mo.part", "Part to build")} htmlFor="mo-part">
            <Input id="mo-part" value={`${order.part_number ?? ""} · ${order.part_name ?? ""}`} disabled />
          </Field>
          {regenerable && editable ? (
            <Field
              label={`${t("column.mo.quantity", "Quantity")}${order.stock_unit_code ? ` (${order.stock_unit_code})` : ""}`}
              htmlFor="mo-quantity"
              error={quantityValid ? undefined : t("mo.quantity.invalid", "Enter a quantity above zero.")}
              hint={t("mo.quantity.regenerate-hint", "Saving a new quantity regenerates the materials.")}
            >
              <Input
                id="mo-quantity"
                type="number"
                step="any"
                min="0"
                value={quantityText ?? String(Number(order.quantity))}
                invalid={!quantityValid}
                onChange={(event) => set({ quantity: event.target.value })}
              />
            </Field>
          ) : (
            <Field label={t("column.mo.quantity", "Quantity")} htmlFor="mo-quantity">
              <Input id="mo-quantity" value={`${formatNumber(order.quantity)} ${order.stock_unit_code ?? ""}`} disabled />
            </Field>
          )}
          <Field label={t("mo.warehouse", "Issue from and receive into")} htmlFor="mo-warehouse">
            <Select
              id="mo-warehouse"
              value={String(draft.warehouse_id)}
              disabled={!editable}
              options={(warehouses.data ?? [])
                .filter((w) => (w.is_active && w.kind === "MANUFACTURING") || w.id === draft.warehouse_id)
                .map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
              onChange={(event) => set({ warehouse_id: Number(event.target.value) })}
            />
          </Field>
          <Field label={t("lot.priority", "Priority")} htmlFor="mo-priority" hint={t("lot.priority-hint", "Lowest first on the floor; empty after the others.")}>
            <Input
              id="mo-priority"
              type="number"
              step="1"
              value={draft.priority ?? ""}
              disabled={!editable}
              onChange={(event) => set({ priority: event.target.value.trim() === "" ? null : Math.trunc(Number(event.target.value)) })}
            />
          </Field>
          <Field label={t("column.mo.name", "Name")} htmlFor="mo-name">
            <Input id="mo-name" value={draft.name ?? ""} disabled={!editable} onChange={(event) => set({ name: text(event.target.value) })} />
          </Field>
          {date("mo-start", t("column.mo.start", "Start"), "start_date")}
          {date("mo-due", t("column.mo.due", "Due"), "due_date")}
          <Field
            label={t("column.mo.lot", "Pipeline lot")}
            htmlFor="mo-lot"
            error={lotValid ? undefined : t("mo.lot.invalid", "A lot is a whole number above zero, or empty.")}
          >
            <Input
              id="mo-lot"
              type="number"
              step="1"
              min="1"
              value={lotText ?? order.production_pipeline_lot_id ?? ""}
              invalid={!lotValid}
              disabled={!editable}
              onChange={(event) => set({ lot: event.target.value })}
            />
          </Field>
          <Field label={t("form.field.note", "Note")} htmlFor="mo-note" className="sm:col-span-2 lg:col-span-3">
            <Textarea id="mo-note" rows={2} value={draft.note ?? ""} disabled={!editable} onChange={(event) => set({ note: text(event.target.value) })} />
          </Field>
        </div>
      </Card>
      <Card className="p-4 sm:p-5">
        <h3 className="mb-3 text-[13px] font-semibold">{t("mo.floor", "On the floor")}</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {date("mo-particle", t("column.mo.particle-cut", "Particle cut"), "particle_cut_date")}
          {date("mo-mdf", t("column.mo.mdf-cut", "MDF cut"), "mdf_cut_date")}
          {date("mo-laval", t("column.mo.arrived-laval", "Arrived at Laval"), "arrived_laval_date")}
          {order.sigm_project_id != null && (
            <div className="flex items-end pb-2">
              <Switch
                checked={draft.is_at_terrebonne}
                disabled={!editable}
                onChange={(checked) => set({ is_at_terrebonne: checked })}
                label={t("lot.sigm-at-terrebonne", "At Terrebonne (SIGM)")}
              />
            </div>
          )}
          <Field label={t("mo.floor-note", "Floor notes")} htmlFor="mo-floor-note" className="sm:col-span-2 lg:col-span-3">
            <Textarea id="mo-floor-note" rows={2} value={draft.floor_note ?? ""} disabled={!editable} onChange={(event) => set({ floor_note: text(event.target.value) })} />
          </Field>
          <Field label={t("mo.floor-comment", "Floor comments")} htmlFor="mo-floor-comment" className="sm:col-span-2 lg:col-span-3">
            <Textarea
              id="mo-floor-comment"
              rows={2}
              value={draft.floor_comment ?? ""}
              disabled={!editable}
              onChange={(event) => set({ floor_comment: text(event.target.value) })}
            />
          </Field>
        </div>
      </Card>
      {editable && (
        <div>
          <Button
            variant="primary"
            disabled={!dirty || !quantityValid || !lotValid}
            loading={save.isPending}
            onClick={() => void submit()}
          >
            {t("button.save", "Save")}
          </Button>
        </div>
      )}
    </div>
  );
}
